// ═══════════════════════════════════════════════════════════════════════════════
// Employer invitation code redeem form (settings + subscription pages)
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  INVITATION_CODE_LENGTH,
  INVITATION_CODE_REDEEM_REASON,
  INVITATION_CODE_VALIDATE_REASON,
  type InvitationCodeRedeemResult,
} from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { settingsApi } from '../../lib/api';
import {
  formatUtcForUserTimezone,
  mapLanguageToLocale,
} from '../../lib/formatUtcDatetime';
import { AppButton } from '../ui/AppButton';
import { useToast } from '../ui/ToastProvider';

type RedeemInvitationCodeFormProps = {
  variant?: 'settings' | 'subscription';
  onRedeemed?: (result: InvitationCodeRedeemResult) => void;
};

const formatPreviewMessage = (
  t: (key: string, params?: Record<string, string | number>) => string,
  preview: { messageKey: string; params: Record<string, string | number> },
  timezone: string | null | undefined,
  locale: string,
): string => {
  const rawEndsAt = String(preview.params.endsAt ?? '');
  const endsAt = formatUtcForUserTimezone(rawEndsAt, timezone, locale);
  const days = Number(preview.params.days ?? 0);
  return t(preview.messageKey, { days, endsAt });
};

export const RedeemInvitationCodeForm = ({
  variant = 'settings',
  onRedeemed,
}: RedeemInvitationCodeFormProps) => {
  const { t, language } = useLanguage();
  const { refreshUser, user } = useAuth();
  const { showSuccessToast } = useToast();
  const userTimezone = user?.settings?.timezone;
  const locale = mapLanguageToLocale(language);
  const [code, setCode] = useState('');
  const [valid, setValid] = useState<boolean | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [previewText, setPreviewText] = useState('');
  const [checkLoading, setCheckLoading] = useState(false);
  const [redeemBusy, setRedeemBusy] = useState(false);
  const [error, setError] = useState('');

  const reasonMessage = useCallback(
    (value: string | null): string => {
      if (!value) {
        return t('settings.invitationCodeInvalid');
      }
      const keyByReason: Record<string, string> = {
        [INVITATION_CODE_VALIDATE_REASON.FORMAT_INVALID]: 'auth.invitationCodeErrorFormat',
        [INVITATION_CODE_VALIDATE_REASON.NOT_FOUND]: 'settings.invitationCodeInvalid',
        [INVITATION_CODE_VALIDATE_REASON.NOT_YET_ACTIVE]: 'auth.invitationCodeErrorNotYetActive',
        [INVITATION_CODE_VALIDATE_REASON.EXPIRED]: 'auth.invitationCodeErrorExpired',
        [INVITATION_CODE_VALIDATE_REASON.DISABLED]: 'auth.invitationCodeErrorDisabled',
        [INVITATION_CODE_VALIDATE_REASON.MAX_USES_REACHED]: 'auth.invitationCodeErrorMaxUses',
        [INVITATION_CODE_REDEEM_REASON.INVITATION_CODE_REGISTRATION_ONLY]:
          'settings.invitationCodeErrorRegistrationOnly',
        [INVITATION_CODE_REDEEM_REASON.INVITATION_CODE_AFTER_REGISTRATION_ONLY]:
          'auth.invitationCodeErrorAfterRegistrationOnly',
        [INVITATION_CODE_REDEEM_REASON.NOT_L1_TRIAL]: 'settings.invitationCodeErrorNotL1Trial',
        [INVITATION_CODE_REDEEM_REASON.L1_TRIAL_ENDED]: 'settings.invitationCodeErrorL1TrialEnded',
        [INVITATION_CODE_REDEEM_REASON.SUBSCRIPTION_ACTIVE]:
          'settings.invitationCodeErrorSubscriptionActive',
        [INVITATION_CODE_REDEEM_REASON.EMAIL_ALREADY_USED]:
          'settings.invitationCodeErrorEmailAlreadyUsed',
        [INVITATION_CODE_REDEEM_REASON.LEVEL_DURATION_REQUIRED]:
          'settings.invitationCodeInvalid',
      };
      const key = keyByReason[value] ?? 'settings.invitationCodeInvalid';
      return t(key);
    },
    [t],
  );

  useEffect(() => {
    if (code.length === 0) {
      setValid(null);
      setReason(null);
      setPreviewText('');
      setCheckLoading(false);
      return;
    }
    if (code.length < INVITATION_CODE_LENGTH) {
      setValid(false);
      setReason(INVITATION_CODE_VALIDATE_REASON.FORMAT_INVALID);
      setPreviewText('');
      setCheckLoading(false);
      return;
    }

    const handle = setTimeout(() => {
      void (async () => {
        setCheckLoading(true);
        setError('');
        try {
          const response = await settingsApi.validateInvitationCode(code);
          if (response.success && response.data) {
            const data = response.data;
            if (data.valid) {
              setValid(true);
              setReason(null);
              setPreviewText(formatPreviewMessage(t, data.preview, userTimezone, locale));
            } else {
              setValid(false);
              setReason(data.reason ?? null);
              setPreviewText('');
            }
          } else {
            setValid(false);
            setReason(INVITATION_CODE_VALIDATE_REASON.NOT_FOUND);
            setPreviewText('');
          }
        } catch {
          setValid(false);
          setReason(INVITATION_CODE_VALIDATE_REASON.NOT_FOUND);
          setPreviewText('');
        } finally {
          setCheckLoading(false);
        }
      })();
    }, 450);

    return () => clearTimeout(handle);
  }, [code, locale, t, userTimezone]);

  const canRedeem = useMemo(
    () => valid === true && !checkLoading && !redeemBusy && code.length === INVITATION_CODE_LENGTH,
    [valid, checkLoading, redeemBusy, code.length],
  );

  const handleCodeChange = useCallback((value: string) => {
    setCode(value.toUpperCase().replace(/[^A-HJ-NP-Z2-9]/g, '').slice(0, INVITATION_CODE_LENGTH));
    setError('');
  }, []);

  const handleRedeem = useCallback(async () => {
    if (!canRedeem) {
      return;
    }
    setRedeemBusy(true);
    setError('');
    try {
      const response = await settingsApi.redeemInvitationCode(code);
      if (!response.success || !response.data) {
        setError(response.error ?? t('settings.invitationCodeRedeemFailed'));
        return;
      }
      await refreshUser();
      showSuccessToast(t('settings.invitationCodeRedeemSuccess'));
      onRedeemed?.(response.data);
      setCode('');
      setValid(null);
      setReason(null);
      setPreviewText('');
    } catch (redeemError) {
      setError(redeemError instanceof Error ? redeemError.message : t('settings.invitationCodeRedeemFailed'));
    } finally {
      setRedeemBusy(false);
    }
  }, [canRedeem, code, onRedeemed, refreshUser, showSuccessToast, t]);

  const containerClass =
    variant === 'subscription'
      ? 'rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900'
      : 'rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/60';

  return (
    <section className={containerClass} aria-labelledby={`redeem-invitation-code-${variant}`}>
      <h2
        id={`redeem-invitation-code-${variant}`}
        className="text-sm font-semibold text-gray-900 dark:text-gray-100"
      >
        {variant === 'subscription'
          ? t('settings.invitationCodeSubscriptionTitle')
          : t('settings.invitationCodeTitle')}
      </h2>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {t('settings.invitationCodeHint')}
      </p>

      <label htmlFor={`redeem-invitation-code-input-${variant}`} className="sr-only">
        {t('settings.invitationCodeInputLabel')}
      </label>
      <input
        id={`redeem-invitation-code-input-${variant}`}
        type="text"
        inputMode="text"
        autoComplete="off"
        value={code}
        onChange={(event) => handleCodeChange(event.target.value)}
        maxLength={INVITATION_CODE_LENGTH}
        placeholder={t('settings.invitationCodeInputPlaceholder')}
        className="mt-3 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 font-mono text-sm tracking-widest text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-950 dark:text-gray-100"
        aria-invalid={valid === false}
        aria-describedby={`redeem-invitation-code-feedback-${variant}`}
      />

      <div id={`redeem-invitation-code-feedback-${variant}`} className="mt-2 min-h-[1.25rem]">
        {checkLoading ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
        ) : null}
        {valid === false && reason ? (
          <p className="text-xs text-red-600 dark:text-red-400" role="alert">
            {reasonMessage(reason)}
          </p>
        ) : null}
        {valid === true && previewText ? (
          <p className="text-xs text-emerald-700 dark:text-emerald-300">{previewText}</p>
        ) : null}
        {error ? (
          <p className="text-xs text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        ) : null}
      </div>

      <AppButton
        type="button"
        variant="primary"
        fullWidth
        className="mt-3"
        onClick={() => {
          void handleRedeem();
        }}
        disabled={!canRedeem}
        aria-label={t('settings.invitationCodeRedeemButton')}
      >
        {redeemBusy ? t('common.loading') : t('settings.invitationCodeRedeemButton')}
      </AppButton>
    </section>
  );
};
